/**
 * @fileoverview Source citations — agent-to-UI sources protocol
 *
 * The universal agent appends a fenced ```morgan-nav block at the end
 * of its reply containing the records it cited. This module:
 *
 * 1. Parses that block out of the raw assistant text (and gracefully
 *    handles the legacy bare-JSON-array shape that's already in
 *    existing threads).
 * 2. Returns the stripped prose plus the parsed source list.
 * 3. Exposes a `<SourceChips />` component that renders the sources as
 *    clickable deep-link chips using the same `searchResultKinds`
 *    metadata the universal search bar uses.
 *
 * Without this layer the JSON shows up verbatim in the chat bubble —
 * see MOR-394 (the “unreadable JSON dump in assistant reply” issue).
 */

import { Link } from "@tanstack/react-router";
import { cn } from "@/utils/misc";
import { getKindMeta } from "./searchResultKinds";
import type { CitedSource } from "./sourceCitations.helpers";

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

interface SourceChipsProps {
  sources: CitedSource[];
  /** Closes wrapping modals (e.g. the Cmd+K palette) on chip click. */
  onChipClick?: () => void;
  /**
   * When provided, called on chip click. Return `true` to indicate the
   * click was handled (suppresses default `<Link>` navigation). Return
   * `false` / `undefined` to let the chip navigate normally.
   */
  onSourceClick?: (source: CitedSource) => boolean | undefined;
  /** Compact inline style for rendering between paragraphs. */
  inline?: boolean;
  className?: string;
}

/**
 * Renders parsed sources as a wrapping row of clickable chips below
 * an assistant message. Each chip deep-links to the owning surface
 * via the shared `buildTo` from `searchResultKinds` — so a document
 * chip jumps into the relevant job's Gather page with the detail
 * panel pre-opened.
 */
export function SourceChips({
  sources,
  onChipClick,
  onSourceClick,
  inline,
  className,
}: SourceChipsProps) {
  if (sources.length === 0) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap gap-1.5",
        inline ? "mb-2 mt-0.5" : "mt-2",
        className,
      )}
    >
      {!inline && (
        <span className="mr-1 mt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
          Sources
        </span>
      )}
      {sources.map((source) => (
        <SourceChip
          key={`${source.kind}-${source.id}`}
          source={source}
          onClick={onChipClick}
          onSourceClick={onSourceClick}
        />
      ))}
    </div>
  );
}

function SourceChip({
  source,
  onClick,
  onSourceClick,
}: {
  source: CitedSource;
  onClick?: () => void;
  onSourceClick?: (source: CitedSource) => boolean | undefined;
}) {
  const meta = getKindMeta(source.kind);
  const Icon = meta.icon;
  const target = meta.buildTo({
    id: source.id,
    jobId: source.jobId,
    orgId: source.orgId,
  });

  const label =
    source.title?.trim() || `${meta.label} ${source.id.slice(0, 6)}`;

  const chipInner = (
    <span className="inline-flex max-w-[280px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/30">
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{label}</span>
      <span className="ml-0.5 shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground/60">
        {meta.label}
      </span>
    </span>
  );

  if (!target) {
    if (onSourceClick) {
      return (
        <button
          type="button"
          onClick={() => {
            onSourceClick(source);
            onClick?.();
          }}
          className="cursor-pointer no-underline"
        >
          {chipInner}
        </button>
      );
    }
    return (
      <span className="opacity-70" title="No navigable target">
        {chipInner}
      </span>
    );
  }

  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      to={target.to as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params={target.params as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search={target.search as any}
      onClick={(e) => {
        if (onSourceClick) {
          const handled = onSourceClick(source);
          if (handled) {
            e.preventDefault();
          }
        }
        onClick?.();
      }}
      className="no-underline"
    >
      {chipInner}
    </Link>
  );
}
